import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { fmtErr } from "../api/axios";
import { RefreshCw, Eye, EyeOff } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("login"); // login | otp | forgot | force-reset
  const [challenge, setChallenge] = useState(null);
  const [emailHint, setEmailHint] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  // Math CAPTCHA state
  const [captcha, setCaptcha] = useState(null);       // { question, token }
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  // Forgot-password cooldown: button disabled for 60 s after each send
  const [forgotCooldown, setForgotCooldown] = useState(0);
  // Cloudflare Turnstile state
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReady, setTurnstileReady] = useState(true); // false when widget errors
  const turnstileRef = useRef(null);

  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const fetchCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setCaptchaAnswer("");
    try {
      const res = await api.get("/auth/captcha");
      setCaptcha(res.data);
    } catch {
      setCaptcha(null);
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  // Show "session expired" banner when redirected here by the 401 interceptor
  useEffect(() => {
    if (sessionStorage.getItem("session_expired")) {
      sessionStorage.removeItem("session_expired");
      setInfo("Your session has expired. Please sign in again.");
    }
  }, []);

  // Tick down the forgot-password cooldown every second
  useEffect(() => {
    if (forgotCooldown <= 0) return;
    const t = setTimeout(() => setForgotCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [forgotCooldown]);

  // Load captcha when the login step is shown
  useEffect(() => {
    if (step === "login") fetchCaptcha();
  }, [step, fetchCaptcha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captcha) {
      setError("Security check not loaded. Please refresh.");
      return;
    }
    // If Turnstile failed to load (domain mismatch etc.) we proceed without it —
    // Math CAPTCHA still protects the endpoint.
    if (turnstileReady && !turnstileToken) {
      setError("Bot verification not complete. Please wait a moment.");
      return;
    }
    setError(""); setInfo(""); setLoading(true);
    try {
      const res = await login(email.trim(), password, captchaAnswer.trim(), captcha.token, turnstileToken);
      if (res?.force_reset_required) {
        setChallenge(res.challenge_id);
        setEmailHint(res.email_hint || "");
        setDevOtp(res.dev_otp || "");
        setStep("force-reset");
        setInfo("Your organisation requires a password reset. Verify the code emailed to you and choose a new password.");
      } else if (res?.twofa_required) {
        setChallenge(res.challenge_id);
        setEmailHint(res.email_hint || "");
        setDevOtp(res.dev_otp || "");
        setStep("otp");
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
      // Reset both challenges on failure so tokens cannot be replayed
      fetchCaptcha();
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await verify2fa(challenge, otp.trim());
      navigate(from, { replace: true });
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setError(""); setInfo("");
    try {
      const r = await api.post("/auth/resend-2fa", { challenge_id: challenge });
      setDevOtp(r.data.dev_otp || "");
      setInfo("A new code has been sent.");
    } catch (err) { setError(fmtErr(err.response?.data?.detail)); }
  };

  const handleForceReset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/force-reset", {
        challenge_id: challenge,
        otp: otp.trim(),
        new_password: newPassword,
      });
      // Session cookie issued by backend — reload auth state and continue
      window.location.href = from;
    } catch (err) {
      setError(fmtErr(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (forgotCooldown > 0) return;
    setError(""); setInfo(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setInfo("If that email exists, a reset link has been sent. Check your inbox.");
      setForgotCooldown(60); // 60-second cooldown before next send
    } catch (err) { setError(fmtErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="login-shell">
      <div className="login-bg" />
      <div className="login-card">
        <div className="brand">
          <div className="brand-mark">S</div>
          <div>
            <div className="brand-name">Snehworld CRM</div>
            <div className="brand-sub">Sales Management Platform</div>
          </div>
        </div>

        {step === "login" && (<>
          <h2 className="login-title">Welcome back</h2>
          <p className="login-sub">Sign in to your account to continue</p>
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email address</label>
              <input id="email" type="email" className="form-input" placeholder="you@snehworld.com"
                value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="login-email-input" />
            </div>
            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <div style={{ position: "relative" }}>
                <input id="password" type={showPassword ? "text" : "password"} className="form-input" placeholder="Enter your password"
                  value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input"
                  style={{ paddingRight: 40 }} />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="login-password-toggle"
                  style={{
                    position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer", padding: 4,
                    color: "var(--text-muted, #64748b)", display: "flex", alignItems: "center",
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Math CAPTCHA */}
            <div className="form-group">
              <label className="form-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>Security check</span>
                <button
                  type="button"
                  onClick={fetchCaptcha}
                  disabled={captchaLoading}
                  title="New question"
                  data-testid="captcha-refresh-btn"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--primary, #4f46e5)", padding: 0, display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}
                >
                  <RefreshCw size={13} style={{ transition: "transform 0.3s", transform: captchaLoading ? "rotate(360deg)" : "none" }} />
                  New question
                </button>
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, background: "var(--surface-2, #f8fafc)", border: "1px solid var(--border, #e2e8f0)", marginBottom: 8 }}>
                <span
                  data-testid="captcha-question"
                  style={{ fontWeight: 700, fontSize: 18, letterSpacing: 3, fontFamily: "monospace", flex: "0 0 auto" }}
                >
                  {captchaLoading ? "Loading…" : (captcha?.question ?? "—")}
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={3}
                  className="form-input"
                  placeholder="?"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value.replace(/[^0-9]/g, ""))}
                  required
                  data-testid="captcha-answer-input"
                  style={{ width: 72, textAlign: "center", fontSize: 18, fontWeight: 600, padding: "6px 8px" }}
                />
              </div>
            </div>

            {error && <div className="error-box" data-testid="login-error">{error}</div>}
            {info && <div className="card" style={{ background: "#e6f7ec", color: "#166534", padding: "8px 12px", marginBottom: 10 }}>{info}</div>}

            {/* Cloudflare Turnstile — invisible bot detection */}
            <Turnstile
              ref={turnstileRef}
              siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
              onSuccess={(token) => { setTurnstileToken(token); setTurnstileReady(true); }}
              onExpire={() => setTurnstileToken("")}
              onError={() => {
                // Domain mismatch (110200) or network failure — degrade gracefully.
                // Send a marker so the backend skips Turnstile; Math CAPTCHA still protects.
                setTurnstileToken("widget_unavailable");
                setTurnstileReady(false);
              }}
              options={{ appearance: "interaction-only", size: "flexible" }}
              data-testid="turnstile-widget"
            />

            <button type="submit" className="btn-primary w-full" disabled={loading || !captcha || (turnstileReady && !turnstileToken)} data-testid="login-submit-btn">
              {loading ? "Signing in..." : "Sign in"}
            </button>
            <button type="button" onClick={() => { setStep("forgot"); setError(""); setInfo(""); }}
              data-testid="forgot-password-link"
              style={{ background: "none", border: "none", color: "var(--primary, #4f46e5)", cursor: "pointer", marginTop: 12, fontSize: 13 }}>
              Forgot password?
            </button>
          </form>
        </>)}

        {step === "otp" && (<>
          <h2 className="login-title">Two-factor verification</h2>
          <p className="login-sub">We emailed a 6-digit code to {emailHint}. Enter it below.</p>
          <form onSubmit={handleVerify} className="login-form">
            <div className="form-group">
              <label htmlFor="otp" className="form-label">One-time code</label>
              <input id="otp" inputMode="numeric" maxLength={6} className="form-input" placeholder="000000"
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} required autoFocus
                data-testid="otp-input" style={{ letterSpacing: 6, fontSize: 20, textAlign: "center" }} />
            </div>
            {devOtp && <div className="card" style={{ background: "#fff7ed", color: "#9a3412", padding: "8px 12px", marginBottom: 10, fontSize: 12 }}>
              Dev hint (until email fully verified): code is <b>{devOtp}</b></div>}
            {error && <div className="error-box" data-testid="otp-error">{error}</div>}
            {info && <div className="card" style={{ background: "#e6f7ec", color: "#166534", padding: "8px 12px", marginBottom: 10 }}>{info}</div>}
            <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="otp-submit-btn">
              {loading ? "Verifying..." : "Verify & sign in"}
            </button>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, fontSize: 13 }}>
              <button type="button" onClick={() => { setStep("login"); setOtp(""); setError(""); }}
                style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer" }}>← Back</button>
              <button type="button" onClick={handleResend} data-testid="otp-resend-btn"
                style={{ background: "none", border: "none", color: "var(--primary, #4f46e5)", cursor: "pointer" }}>Resend code</button>
            </div>
          </form>
        </>)}

        {step === "force-reset" && (<>
          <h2 className="login-title">Password reset required</h2>
          <p className="login-sub">For security, your organisation requires a new password. We emailed a 6-digit verification code to {emailHint}.</p>
          <form onSubmit={handleForceReset} className="login-form">
            <div className="form-group">
              <label htmlFor="fr-otp" className="form-label">Verification code</label>
              <input id="fr-otp" inputMode="numeric" maxLength={6} className="form-input" placeholder="000000"
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} required autoFocus
                data-testid="force-reset-otp-input" style={{ letterSpacing: 6, fontSize: 20, textAlign: "center" }} />
            </div>
            <div className="form-group">
              <label htmlFor="fr-new" className="form-label">New password</label>
              <input id="fr-new" type="password" className="form-input" placeholder="Min 10 chars, upper, lower, digit, symbol"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
                data-testid="force-reset-password-input" />
            </div>
            <div className="form-group">
              <label htmlFor="fr-confirm" className="form-label">Confirm new password</label>
              <input id="fr-confirm" type="password" className="form-input" placeholder="Repeat new password"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                data-testid="force-reset-confirm-input" />
            </div>
            {devOtp && <div className="card" style={{ background: "#fff7ed", color: "#9a3412", padding: "8px 12px", marginBottom: 10, fontSize: 12 }}>
              Dev hint (until email fully verified): code is <b>{devOtp}</b></div>}
            {error && <div className="error-box" data-testid="force-reset-error">{error}</div>}
            {info && <div className="card" style={{ background: "#e6f7ec", color: "#166534", padding: "8px 12px", marginBottom: 10 }}>{info}</div>}
            <button type="submit" className="btn-primary w-full" disabled={loading} data-testid="force-reset-submit-btn">
              {loading ? "Resetting..." : "Verify & set new password"}
            </button>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, fontSize: 13 }}>
              <button type="button" onClick={() => { setStep("login"); setOtp(""); setNewPassword(""); setConfirmPassword(""); setError(""); setInfo(""); }}
                style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer" }}>← Back</button>
              <button type="button" onClick={handleResend} data-testid="force-reset-resend-btn"
                style={{ background: "none", border: "none", color: "var(--primary, #4f46e5)", cursor: "pointer" }}>Resend code</button>
            </div>
          </form>
        </>)}

        {step === "forgot" && (<>
          <h2 className="login-title">Reset your password</h2>
          <p className="login-sub">Enter your email and we'll send you a reset link.</p>
          <form onSubmit={handleForgot} className="login-form">
            <div className="form-group">
              <label htmlFor="femail" className="form-label">Email address</label>
              <input id="femail" type="email" className="form-input" placeholder="you@snehworld.com"
                value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="forgot-email-input" />
            </div>
            {error && <div className="error-box">{error}</div>}
            {info && <div className="card" style={{ background: "#e6f7ec", color: "#166534", padding: "8px 12px", marginBottom: 10 }} data-testid="forgot-info">{info}</div>}
            <button type="submit" className="btn-primary w-full"
              disabled={loading || forgotCooldown > 0}
              data-testid="forgot-submit-btn">
              {loading
                ? "Sending..."
                : forgotCooldown > 0
                  ? `Resend in ${forgotCooldown}s`
                  : "Send reset link"}
            </button>
            <button type="button" onClick={() => { setStep("login"); setError(""); setInfo(""); }}
              style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", marginTop: 12, fontSize: 13 }}>← Back to sign in</button>
          </form>
        </>)}
      </div>
    </div>
  );
}
